import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  TextField,
  Collapse,
  IconButton,
} from '@mui/material';
import { Sync, ExpandMore, ExpandLess, Download } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { evitalrxAPI, lookupAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Store } from '../types';

interface DistributorSummary {
  distributor_name: string;
  bill_count: number;
  total_amount: string;
  oldest_bill_date: string | null;
  last_synced_at: string | null;
}

interface EvitalrxPurchase {
  id: number;
  store_id: number;
  bill_no: string;
  bill_date: string | null;
  entry_date: string | null;
  entry_by: string;
  distributor_name: string;
  bill_amount: string;
  evitalrx_payment_method: string;
  oms_status: 'due' | 'paid';
  oms_payment_method: string | null;
  oms_paid_at: string | null;
  oms_paid_by: string | null;
  synced_at: string | null;
  source?: 'evitalrx' | 'pos';
}

interface Totals {
  bill_count: number;
  total_amount: number;
}

interface PurchasePaymentsProps {
  selectedStore: Store | null;
}

const PAYMENT_METHODS = ['UPI', 'Cash', 'NEFT', 'RTGS', 'Cheque'];
const storeOptions = [
  { value: 'all', label: 'All Stores' },
];

function fmtAmount(val: string | number) {
  return `₹${Number(val).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function fmtDate(val: string | null) {
  if (!val) return '—';
  return dayjs(val).format('DD-MMM-YY');
}

// ─── Distributor row with expandable bill list ────────────────────────────────
function DistributorRow({
  row,
  storeId,
  onPaid,
}: {
  row: DistributorSummary;
  storeId: number | 'all';
  onPaid: () => void;
}) {
  const { currentUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [bills, setBills] = useState<EvitalrxPurchase[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);
  const [payMethod, setPayMethod] = useState('UPI');
  const [marking, setMarking] = useState(false);
  const [error, setError] = useState('');

  const loadBills = async () => {
    if (bills.length > 0) return;
    setLoading(true);
    try {
      const data: EvitalrxPurchase[] = await evitalrxAPI.getPurchases({
        store_id: storeId,
        status: 'due',
        distributor_name: row.distributor_name,
      });
      setBills(data);
      setSelected(data.filter(b => b.source !== 'pos').map(b => b.id));
    } catch {
      setError('Failed to load bills');
    } finally {
      setLoading(false);
    }
  };

  const handleExpand = () => {
    if (!open) loadBills();
    setOpen(o => !o);
  };

  const toggleSelect = (id: number) =>
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const handleMarkPaid = async () => {
    if (selected.length === 0) return;
    setMarking(true);
    setError('');
    try {
      await evitalrxAPI.markPaid(selected, payMethod, currentUser?.email || '');
      onPaid();
    } catch {
      setError('Failed to mark as paid');
    } finally {
      setMarking(false);
    }
  };

  // POS bills (stores on the FirstShield POS) are paid in the POS, not marked here
  const payable = bills.filter(b => b.source !== 'pos');

  const selectedTotal = bills
    .filter(b => selected.includes(b.id))
    .reduce((s, b) => s + parseFloat(b.bill_amount), 0);

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: 'pointer', '& td': { fontWeight: open ? 600 : 400 } }}
        onClick={handleExpand}
      >
        <TableCell>
          <IconButton size="small">{open ? <ExpandLess /> : <ExpandMore />}</IconButton>
        </TableCell>
        <TableCell>{row.distributor_name || '(Unknown)'}</TableCell>
        <TableCell align="center">{row.bill_count}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 700, color: '#d32f2f' }}>
          {fmtAmount(row.total_amount)}
        </TableCell>
        <TableCell align="center">
          {row.oldest_bill_date ? (
            <Chip
              label={fmtDate(row.oldest_bill_date)}
              size="small"
              color={
                dayjs().diff(dayjs(row.oldest_bill_date), 'day') > 7 ? 'error' : 'warning'
              }
              variant="outlined"
            />
          ) : '—'}
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={5} sx={{ p: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ p: 2, bgcolor: '#fafafa', borderLeft: '4px solid #1976d2' }}>
              {loading && <CircularProgress size={24} />}
              {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

              {bills.length > 0 && (
                <>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={payable.length > 0 && selected.length === payable.length}
                            indeterminate={selected.length > 0 && selected.length < payable.length}
                            disabled={payable.length === 0}
                            onChange={e => setSelected(e.target.checked ? payable.map(b => b.id) : [])}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>Bill No</TableCell>
                        <TableCell>Bill Date</TableCell>
                        <TableCell>Entry By</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bills.map(b => (
                        <TableRow key={b.id} selected={selected.includes(b.id)}>
                          <TableCell padding="checkbox">
                            {b.source === 'pos' ? (
                              <Chip label="POS" size="small" variant="outlined" title="Pay this bill in the POS" />
                            ) : (
                              <Checkbox
                                checked={selected.includes(b.id)}
                                onChange={() => toggleSelect(b.id)}
                                size="small"
                              />
                            )}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{b.bill_no}</TableCell>
                          <TableCell>{fmtDate(b.bill_date)}</TableCell>
                          <TableCell>{b.entry_by || '—'}</TableCell>
                          <TableCell align="right">{fmtAmount(b.bill_amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {payable.length < bills.length && (
                    <Alert severity="info" sx={{ mt: 1.5, py: 0 }}>
                      Bills marked POS are from stores on the FirstShield POS — record their payment in the POS.
                    </Alert>
                  )}
                  {payable.length > 0 && <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {selected.length} bill{selected.length !== 1 ? 's' : ''} selected —{' '}
                      <span style={{ color: '#d32f2f' }}>{fmtAmount(selectedTotal)}</span>
                    </Typography>

                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <InputLabel>Payment via</InputLabel>
                      <Select
                        value={payMethod}
                        label="Payment via"
                        onChange={e => setPayMethod(e.target.value)}
                      >
                        {PAYMENT_METHODS.map(m => (
                          <MenuItem key={m} value={m}>{m}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Button
                      variant="contained"
                      size="small"
                      disabled={selected.length === 0 || marking}
                      onClick={handleMarkPaid}
                      sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
                    >
                      {marking ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                      Mark {selected.length > 0 ? `${selected.length} ` : ''}as Paid
                    </Button>
                  </Box>}
                </>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
const PurchasePayments: React.FC<PurchasePaymentsProps> = ({ selectedStore }) => {
  const { currentUser } = useAuth();
  const [tab, setTab] = useState(0);
  const [stores, setStores] = useState<Store[]>([]);

  useEffect(() => {
    lookupAPI.getStores().then(setStores).catch(() => {});
  }, []);

  const storeOptions = [
    { value: 'all' as const, label: 'All Stores' },
    ...stores.map(s => ({ value: s.id, label: s.store_name })),
  ];

  // Sync controls
  const [syncStore, setSyncStore] = useState<number | 'all'>(
    selectedStore ? selectedStore.id : 'all'
  );
  const [syncFrom, setSyncFrom] = useState<Dayjs | null>(dayjs().subtract(1, 'day'));
  const [syncTo, setSyncTo] = useState<Dayjs | null>(dayjs());
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ synced: number; skipped: number } | null>(null);
  const [syncError, setSyncError] = useState('');

  // Summary tab
  const [summaryStore, setSummaryStore] = useState<number | 'all'>(
    selectedStore ? selectedStore.id : 'all'
  );
  const [summary, setSummary] = useState<DistributorSummary[]>([]);
  const [totals, setTotals] = useState<Totals>({ bill_count: 0, total_amount: 0 });
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [lastSynced, setLastSynced] = useState<string | null>(null);

  // All bills tab
  const [billsStore, setBillsStore] = useState<number | 'all'>(
    selectedStore ? selectedStore.id : 'all'
  );
  const [billsStatus, setBillsStatus] = useState<'due' | 'paid' | 'all'>('due');
  const [billsFrom, setBillsFrom] = useState<Dayjs | null>(dayjs().subtract(1, 'day'));
  const [billsTo, setBillsTo] = useState<Dayjs | null>(dayjs());
  const [distFilter, setDistFilter] = useState('');
  const [bills, setBills] = useState<EvitalrxPurchase[]>([]);
  const [billsLoading, setBillsLoading] = useState(false);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const data = await evitalrxAPI.getSummary(summaryStore, 'due');
      setSummary(data.distributors || []);
      setTotals(data.totals || { bill_count: 0, total_amount: 0 });
      if (data.distributors?.length > 0 && data.distributors[0].last_synced_at) {
        setLastSynced(data.distributors[0].last_synced_at);
      }
    } catch {
      setSummary([]);
    } finally {
      setSummaryLoading(false);
    }
  }, [summaryStore]);

  const loadBills = useCallback(async () => {
    setBillsLoading(true);
    try {
      const data = await evitalrxAPI.getPurchases({
        store_id: billsStore,
        status: billsStatus,
        from_date: billsFrom?.format('YYYY-MM-DD'),
        to_date: billsTo?.format('YYYY-MM-DD'),
        distributor_name: distFilter || undefined,
      });
      setBills(data);
    } catch {
      setBills([]);
    } finally {
      setBillsLoading(false);
    }
  }, [billsStore, billsStatus, billsFrom, billsTo, distFilter]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { if (tab === 1) loadBills(); }, [tab, loadBills]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    setSyncError('');
    try {
      const res = await evitalrxAPI.sync(
        syncStore,
        syncFrom?.format('YYYY-MM-DD'),
        syncTo?.format('YYYY-MM-DD'),
      );
      setSyncResult({ synced: res.synced, skipped: res.skipped });
      loadSummary();
      if (tab === 1) loadBills();
    } catch (e: any) {
      setSyncError(e?.response?.data?.message || 'Sync failed. Check scraper logs.');
    } finally {
      setSyncing(false);
    }
  };

  const exportCSV = () => {
    const headers = ['Bill No', 'Bill Date', 'Distributor', 'Amount', 'Status', 'Payment Method', 'Paid At', 'Paid By'];
    const rows = bills.map(b => [
      b.bill_no,
      fmtDate(b.bill_date),
      b.distributor_name,
      b.bill_amount,
      b.oms_status,
      b.oms_payment_method || b.evitalrx_payment_method || '',
      b.oms_paid_at ? fmtDate(b.oms_paid_at) : '',
      b.oms_paid_by || '',
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `purchases_${dayjs().format('YYYY-MM-DD')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Sync bar */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Sync from evitalrx:</Typography>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Store</InputLabel>
          <Select
            value={syncStore}
            label="Store"
            onChange={e => setSyncStore(e.target.value as number | 'all')}
          >
            {storeOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>

        <DatePicker
          label="From"
          value={syncFrom}
          onChange={setSyncFrom}
          slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
        />
        <DatePicker
          label="To"
          value={syncTo}
          onChange={setSyncTo}
          slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
        />

        <Button
          variant="contained"
          startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <Sync />}
          onClick={handleSync}
          disabled={syncing}
        >
          {syncing ? 'Syncing…' : 'Sync Now'}
        </Button>

        {syncResult && (
          <Alert severity="success" sx={{ py: 0 }}>
            Synced {syncResult.synced} new bills, {syncResult.skipped} already exist
          </Alert>
        )}
        {syncError && <Alert severity="error" sx={{ py: 0 }}>{syncError}</Alert>}
        <Typography variant="caption" color="text.secondary">
          All stores are on the FirstShield POS — bills after each store’s switch date come live from the POS, no sync needed.
        </Typography>

        {lastSynced && (
          <Typography variant="caption" color="textSecondary" sx={{ ml: 'auto' }}>
            Last synced: {dayjs(lastSynced).format('DD-MMM-YY HH:mm')}
          </Typography>
        )}
      </Paper>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tab label="Pending by Distributor" />
        <Tab label="All Bills" />
      </Tabs>

      {/* ── Tab 0: Pending Summary ── */}
      {tab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Store</InputLabel>
              <Select
                value={summaryStore}
                label="Store"
                onChange={e => { setSummaryStore(e.target.value as number | 'all'); }}
              >
                {storeOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
            <Button size="small" onClick={loadSummary} disabled={summaryLoading}>Refresh</Button>
          </Box>

          {summaryLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
          ) : summary.length === 0 ? (
            <Alert severity="info">No pending bills found. Run a sync first.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                  <TableRow>
                    <TableCell width={40} />
                    <TableCell>Distributor</TableCell>
                    <TableCell align="center">Bills Due</TableCell>
                    <TableCell align="right">Amount Due</TableCell>
                    <TableCell align="center">Oldest Bill</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.map(row => (
                    <DistributorRow
                      key={row.distributor_name}
                      row={row}
                      storeId={summaryStore}
                      onPaid={loadSummary}
                    />
                  ))}

                  {/* Totals row */}
                  <TableRow sx={{ bgcolor: '#fce4ec' }}>
                    <TableCell />
                    <TableCell sx={{ fontWeight: 700 }}>TOTAL PENDING</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>{totals.bill_count}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#d32f2f' }}>
                      {fmtAmount(totals.total_amount)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* ── Tab 1: All Bills ── */}
      {tab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Store</InputLabel>
              <Select value={billsStore} label="Store" onChange={e => setBillsStore(e.target.value as number | 'all')}>
                {storeOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select value={billsStatus} label="Status" onChange={e => setBillsStatus(e.target.value as 'due' | 'paid' | 'all')}>
                <MenuItem value="due">Due only</MenuItem>
                <MenuItem value="paid">Paid only</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>

            <DatePicker
              label="From"
              value={billsFrom}
              onChange={setBillsFrom}
              slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
            />
            <DatePicker
              label="To"
              value={billsTo}
              onChange={setBillsTo}
              slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
            />

            <TextField
              size="small"
              label="Distributor"
              value={distFilter}
              onChange={e => setDistFilter(e.target.value)}
              sx={{ width: 180 }}
            />

            <Button variant="outlined" size="small" onClick={loadBills} disabled={billsLoading}>
              Search
            </Button>
            <Button
              size="small"
              startIcon={<Download />}
              onClick={exportCSV}
              disabled={bills.length === 0}
            >
              Export CSV
            </Button>
          </Box>

          {billsLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                  <TableRow>
                    <TableCell>Bill Date</TableCell>
                    <TableCell>Distributor</TableCell>
                    <TableCell>Bill No</TableCell>
                    <TableCell>Entry By</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell>Payment</TableCell>
                    <TableCell>Paid At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bills.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No bills found
                      </TableCell>
                    </TableRow>
                  ) : bills.map(b => (
                    <TableRow key={b.id} hover>
                      <TableCell>{fmtDate(b.bill_date)}</TableCell>
                      <TableCell>{b.distributor_name || '—'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{b.bill_no}</TableCell>
                      <TableCell>{b.entry_by || '—'}</TableCell>
                      <TableCell align="right">{fmtAmount(b.bill_amount)}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={b.oms_status === 'paid' ? 'PAID' : 'DUE'}
                          size="small"
                          color={b.oms_status === 'paid' ? 'success' : 'error'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {b.oms_payment_method || b.evitalrx_payment_method || '—'}
                      </TableCell>
                      <TableCell>
                        {b.oms_paid_at ? (
                          <Typography variant="caption">
                            {fmtDate(b.oms_paid_at)}<br />
                            <span style={{ color: '#666' }}>{b.oms_paid_by?.split('@')[0]}</span>
                          </Typography>
                        ) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {bills.length === 500 && (
                <Box sx={{ p: 1, textAlign: 'center' }}>
                  <Typography variant="caption" color="textSecondary">
                    Showing first 500 results — use filters to narrow down
                  </Typography>
                </Box>
              )}
            </TableContainer>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PurchasePayments;
